import { useMutation, useQuery } from "@apollo/client";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { HtmlEditor } from "../components/HtmlEditor";
import { HtmlPagePreview } from "../components/HtmlPagePreview";
import { DELETE_SHOP_METADATA, UPDATE_SHOP_METADATA } from "../mutations";
import { HTML_WIDGETS } from "../queries";
import { HtmlPage, HtmlPageFormData } from "../types";
import { htmlManagerListUrl } from "../urls";
import {
  createDefaultPage,
  generatePageId,
  generateSlugFromName,
  parseHtmlPageFromMetadata,
  serializeHtmlPageToMetadata,
} from "../utils";

export interface HtmlManagerDetailsViewProps {
  id: string;
}

export const HtmlManagerDetailsView: React.FC<HtmlManagerDetailsViewProps> = ({ id }) => {
  const notify = useNotifier();
  const isNew = id === "add";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const { data, loading } = useQuery(HTML_WIDGETS, {
    fetchPolicy: "cache-and-network",
  });

  const [updateMetadata] = useMutation(UPDATE_SHOP_METADATA);
  const [deleteMetadata] = useMutation(DELETE_SHOP_METADATA);

  const page = useMemo(() => {
    if (isNew) {
      const defaultPage = createDefaultPage();

      return defaultPage;
    }

    if (!data?.shop?.metadata) {
      return null;
    }

    const metadataItem = data.shop.metadata.find((item: any) => item.key === `html_widget_${id}`);

    if (!metadataItem) {
      return null;
    }

    const parsedPage = parseHtmlPageFromMetadata(metadataItem.key, metadataItem.value);

    return parsedPage;
  }, [data, id, isNew]);

  const { control, handleSubmit, watch, reset } = useForm<HtmlPageFormData>({
    defaultValues: page || createDefaultPage(),
  });

  // Reset form when page changes
  useEffect(() => {
    if (page) {
      reset(page);
    }
  }, [page, reset]);

  const watchedValues = watch();

  const onSubmit = async (formData: HtmlPageFormData) => {
    setIsSubmitting(true);

    try {
      const generatedId = isNew ? generatePageId() : id;

      const pageData: HtmlPage = {
        id: generatedId,
        ...formData,
        createdAt: page?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const shopId = data?.shop?.id;

      if (!shopId) {
        notify({
          status: "error",
          text: "Error: Shop ID not found. Please refresh the page and try again.",
        });
        setIsSubmitting(false);

        return;
      }

      // Save to metadata
      const { key, value } = serializeHtmlPageToMetadata(pageData);
      
      const metadataResult = await updateMetadata({
        variables: {
          id: shopId,
          input: [{ key, value }],
        },
      });

      if (metadataResult.data?.updateMetadata?.errors?.length > 0) {
        console.error("Metadata GraphQL errors:", metadataResult.data.updateMetadata.errors);
        notify({
          status: "error",
          text:
            "Error saving page metadata: " +
            metadataResult.data.updateMetadata.errors.map((e: any) => e.message).join(", "),
        });

        return;
      }

      notify({
        status: "success",
        text: isNew ? "Page created successfully!" : "Page saved successfully!",
      });

      // Navigate back to list
      window.location.href = htmlManagerListUrl();
    } catch (error: any) {
      console.error("Failed to save page:", error);
      notify({
        status: "error",
        text: `Failed to save page: ${error?.message || "Unknown error"}. Please try again.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isNew || !page) return;

    if (
      !confirm(`Are you sure you want to delete "${page.name}"? This action cannot be undone.`)
    ) {
      return;
    }

    try {
      await deleteMetadata({
        variables: {
          id: data?.shop?.id || "U2hvcDox",
          keys: [`html_widget_${id}`],
        },
      });

      notify({
        status: "success",
        text: `Page "${page.name}" has been deleted successfully.`,
      });

      // Navigate back to list
      window.location.href = htmlManagerListUrl();
    } catch (error) {
      console.error("Failed to delete page:", error);
      notify({
        status: "error",
        text: "Failed to delete page. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <Box padding={4} textAlign="center">
        <Text>Loading page...</Text>
      </Box>
    );
  }

  if (!isNew && !page) {
    return (
      <Box padding={4} textAlign="center">
        <Text>Page not found</Text>
        <Button
          variant="secondary"
          onClick={() => (window.location.href = htmlManagerListUrl())}
          marginTop={2}
        >
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* Sticky Header */}
      <Box 
        style={{ 
          position: "sticky", 
          top: 0, 
          zIndex: 100,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
        }}
      >
        <TopNav
          href={htmlManagerListUrl()}
          title={isNew ? "Create HTML Page" : `Edit: ${page?.name}`}
        >
          <Button
            variant={showPreview ? "secondary" : "primary"}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
        </TopNav>
      </Box>

      <Box paddingX={6} paddingY={6}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            gap={6}
            __flexDirection={{ mobile: "column", desktop: "row" }}
            __alignItems="flex-start"
          >
            {/* Left Section - Form Controls (Dynamic Width) */}
            <Box 
              display="flex" 
              flexDirection="column" 
              gap={6} 
              __flex={showPreview ? "0 0 400px" : "1"}
              __width={{ mobile: "100%", desktop: showPreview ? "400px" : "auto" }}
              __maxWidth={showPreview ? "400px" : "none"}
            >
              {/* Page Settings Card */}
              <Box
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
                }}
              >
                <Box 
                  paddingX={6} 
                  paddingY={4} 
                  style={{ borderBottom: "1px solid #f1f5f9" }}
                >
                  <Text size={4} fontWeight="bold" color="default1">
                    Page Settings
                  </Text>
                </Box>
                <Box padding={6}>
                  <Box display="flex" flexDirection="column" gap={4}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Page name is required" }}
                      render={({ field, fieldState }) => (
                        <Box>
                          <Text size={3} fontWeight="medium" marginBottom={2}>
                            Name *
                          </Text>
                          <Input
                            {...field}
                            placeholder="Enter page name"
                            error={!!fieldState.error}
                            style={{ fontSize: "14px" }}
                          />
                          {fieldState.error && (
                            <Text color="critical1" size={2} marginTop={1}>
                              {fieldState.error.message}
                            </Text>
                          )}
                        </Box>
                      )}
                    />

                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <Text size={3} fontWeight="medium" marginBottom={2}>
                            Description
                          </Text>
                          <Input {...field} placeholder="Enter page description" />
                        </Box>
                      )}
                    />

                    <Controller
                      name="slug"
                      control={control}
                      rules={{ 
                        required: "Slug is required",
                        pattern: {
                          value: /^[a-z0-9-]+$/,
                          message: "Slug must contain only lowercase letters, numbers, and hyphens"
                        }
                      }}
                      render={({ field, fieldState }) => (
                        <Box>
                          <Text size={3} fontWeight="medium" marginBottom={2}>
                            Slug * <Text size={2} color="default2">(URL identifier)</Text>
                          </Text>
                          <Input
                            {...field}
                            placeholder="url-friendly-identifier"
                            error={!!fieldState.error}
                            onChange={(e) => {
                              const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');

                              field.onChange(value);
                            }}
                          />
                          <Text size={2} color="default2" marginTop={1}>
                            Frontend URL: yoursite.com/{field.value || 'your-slug'}
                          </Text>
                          {!field.value && watchedValues.name && (
                            <Button
                              variant="tertiary"
                              type="button"
                              size="small"
                              marginTop={2}
                              onClick={() => {
                                const generatedSlug = generateSlugFromName(watchedValues.name);

                                field.onChange(generatedSlug);
                              }}
                            >
                              Generate from name
                            </Button>
                          )}
                          {fieldState.error && (
                            <Text color="critical1" size={2} marginTop={1}>
                              {fieldState.error.message}
                            </Text>
                          )}
                        </Box>
                      )}
                    />

                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <Text size={3} fontWeight="medium" marginBottom={2}>
                            Type
                          </Text>
                          <Input 
                            {...field} 
                            placeholder="e.g., calculator, form, banner"
                          />
                        </Box>
                      )}
                    />

                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={field.value}
                              onChange={field.onChange}
                              style={{ transform: "scale(1.2)" }}
                            />
                            <Text size={3} fontWeight="medium">Active</Text>
                          </label>
                        </Box>
                      )}
                    />
                  </Box>
                </Box>
              </Box>

              {/* HTML Content Card */}
              <Box
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
                }}
              >
                <Box 
                  paddingX={6} 
                  paddingY={4} 
                  style={{ borderBottom: "1px solid #f1f5f9" }}
                >
                  <Text size={4} fontWeight="bold" color="default1">
                    HTML Content
                  </Text>
                </Box>
                <Box padding={6}>
                  <Controller
                    name="html"
                    control={control}
                    render={({ field }) => (
                      <HtmlEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        language="html"
                        placeholder="Enter your HTML content here..."
                        height="200px"
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* JavaScript Card */}
              <Box
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
                }}
              >
                <Box 
                  paddingX={6} 
                  paddingY={4} 
                  style={{ borderBottom: "1px solid #f1f5f9" }}
                >
                  <Text size={4} fontWeight="bold" color="default1">
                    JavaScript
                  </Text>
                </Box>
                <Box padding={6}>
                  <Controller
                    name="javascript"
                    control={control}
                    render={({ field }) => (
                      <HtmlEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        language="javascript"
                        placeholder="Enter your JavaScript code here..."
                        height="200px"
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* CSS Styles Card */}
              <Box
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
                }}
              >
                <Box 
                  paddingX={6} 
                  paddingY={4} 
                  style={{ borderBottom: "1px solid #f1f5f9" }}
                >
                  <Text size={4} fontWeight="bold" color="default1">
                    CSS Styles
                  </Text>
                </Box>
                <Box padding={6}>
                  <Controller
                    name="css"
                    control={control}
                    render={({ field }) => (
                      <HtmlEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        language="css"
                        placeholder="Enter your CSS styles here..."
                        height="150px"
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>

            {/* Right Section - Preview Area */}
            {showPreview && (
              <Box
                __flex="1"
                __minWidth="0"
                __width={{ mobile: "100%", desktop: "auto" }}
              >
                <Box position="sticky" __top="100px">
                  <HtmlPagePreview page={watchedValues} />
                </Box>
              </Box>
            )}
          </Box>

          {/* Fixed Bottom Save Bar */}
          <Box 
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "white",
              borderTop: "1px solid #e5e7eb",
              padding: "16px 24px",
              zIndex: 50,
              boxShadow: "0 -1px 3px rgba(0, 0, 0, 0.1)"
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                {!isNew && (
                  <Button 
                    variant="error" 
                    onClick={handleDelete}
                    disabled={isSubmitting}
                  >
                    Delete Page
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={3}>
                <Button 
                  variant="secondary" 
                  onClick={() => (window.location.href = htmlManagerListUrl())}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : (isNew ? "Create Page" : "Save Changes")}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Add bottom padding to prevent content from being hidden behind fixed save bar */}
          <Box style={{ height: "80px" }} />
        </form>
      </Box>
    </Box>
  );
};